/**
 * Compress an image File to fit within Anthropic's API limits:
 * - Max 5 MB base64 payload
 * - Max 8000px per dimension (we target 7500px for safety)
 *
 * Uses the browser Canvas API to re-encode the image as JPEG at progressively
 * lower quality / smaller dimensions until it fits.
 *
 * Non-image files pass through unchanged.
 */

const ANTHROPIC_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const SAFE_TARGET_BYTES = 4.5 * 1024 * 1024; // 4.5 MB — leave headroom
const MAX_DIMENSION = 7500; // Anthropic limit is 8000, use 7500 for safety

/**
 * Returns the File as-is if it's not an image or already within limits.
 * Otherwise returns a compressed JPEG File.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  // Only compress raster images
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  const needsSizeReduction = file.size > ANTHROPIC_MAX_BYTES;
  const needsDimensionReduction =
    width > MAX_DIMENSION || height > MAX_DIMENSION;

  // Already within all limits — no work needed
  if (!needsSizeReduction && !needsDimensionReduction) {
    bitmap.close();
    return file;
  }

  console.log(
    `[compressImage] "${file.name}" ${width}x${height} ${(file.size / 1024 / 1024).toFixed(1)} MB — compressing` +
      (needsDimensionReduction ? ' (dimensions exceed limit)' : '') +
      (needsSizeReduction ? ' (size exceeds limit)' : ''),
  );

  // Calculate initial scale to bring dimensions within the max
  let dimensionScale = 1;
  if (needsDimensionReduction) {
    dimensionScale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
  }

  let quality = 0.85;
  const MIN_QUALITY = 0.4;
  let scale = dimensionScale;
  let blob: Blob | null = null;

  // Always run at least one pass (to enforce dimension limit even if size is fine)
  do {
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = new OffscreenCanvas(targetWidth, targetHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) break;

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });

    console.log(
      `[compressImage] quality=${quality.toFixed(2)} scale=${Math.round(scale * 100)}% → ${(blob.size / 1024 / 1024).toFixed(2)} MB (${targetWidth}x${targetHeight})`,
    );

    if (blob.size <= SAFE_TARGET_BYTES) break;

    // Reduce quality first, then start scaling down
    if (quality > MIN_QUALITY) {
      quality -= 0.15;
    } else if (scale > 0.3) {
      scale -= 0.15;
      quality = 0.7; // Reset quality for the smaller size
    } else {
      break; // Can't reduce further
    }
  } while (quality >= MIN_QUALITY);

  bitmap.close();

  if (!blob || blob.size > ANTHROPIC_MAX_BYTES) {
    console.warn(
      '[compressImage] Could not compress below limit, returning original',
    );
    return file;
  }

  // Build a new File with the original name (but .jpg extension)
  const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
  const compressedFile = new File([blob], `${nameWithoutExt}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  console.log(
    `[compressImage] Compressed ${(file.size / 1024 / 1024).toFixed(1)} MB → ${(compressedFile.size / 1024 / 1024).toFixed(1)} MB`,
  );

  return compressedFile;
}
